import { createSelector } from '@reduxjs/toolkit';
import { DefaultRootState } from 'react-redux';
import { roundedDurationSelector } from '../../ecg/selectors/ecg.selectors';
import { computeInterval } from '../../episodeMeasurement/utils/episode-measurement.utils';
import {
  smallestStripSelector,
  timestampSelector,
} from '../../navigation/selectors/navigation.selectors';
import { MINUTE } from '../../utils/time';
import { getCursor } from '../../wave/utils/update.utils';
import { MINIMUM_RULER_DURATION } from '../utils/ruler.utils';

export const rulerSelector = ({ ruler }: DefaultRootState) => ruler;

export const rulerUpdateModeSelector = createSelector(
  rulerSelector,
  ({ updateMode }) => updateMode,
);

export const getRulerCursor = createSelector(rulerSelector, ({ updateMode }) =>
  getCursor(updateMode),
);

export const previousRulerTimestampSelector = createSelector(
  timestampSelector,
  rulerSelector,
  (timestamp, state) =>
    state.updateMode === 'move' ? state.previousTimestamp : timestamp,
);

export const isMovingRulerSelector = createSelector(
  rulerUpdateModeSelector,
  (updateMode) => updateMode === 'move',
);

export const isUpdatingRulerSelector = createSelector(
  rulerSelector,
  ({ updateMode }) => updateMode != null,
);

export const rulerRangeSelector = createSelector(
  rulerSelector,
  ({ start, end }) => [Math.min(start, end), Math.max(start, end)],
);

export const rulerPointsSelector = createSelector(
  rulerRangeSelector,
  roundedDurationSelector,
  smallestStripSelector,
  ([start, end], ecgDuration, strip) => {
    let sOnset = 0,
      sOffset = 0;
    if (strip != null) {
      sOnset = strip.onset;
      sOffset = strip.offset;
    }
    const rulerDuration = end - start;
    const absoluteStart = start % rulerDuration;
    const points: number[] = [];
    let current = absoluteStart;

    while (
      current < ecgDuration + sOffset + sOnset &&
      rulerDuration > MINIMUM_RULER_DURATION
    ) {
      points.push(current);
      current += rulerDuration;
    }
    return points;
  },
);

export const rulerMetricsSelector = createSelector(
  rulerRangeSelector,
  ([start, end]) => {
    const rulerDuration = computeInterval(start, end);
    const beatsPerMinute = Math.round(MINUTE / rulerDuration);
    return `${rulerDuration}ms, ${beatsPerMinute}bpm`;
  },
);
